import {
  MealActivityTypes,
  sessionEstimateItemTitles,
  TravelActivityTypes,
  WorkActivityTypes,
  WorkTimeType,
} from '@shared/types';
import { getIsAFMPerformer, getIsExtra, getIsPrincipal } from '@shared/utils';
import {
  CommercialType,
  Location,
  Role,
  Script,
  Session,
  SessionActivity,
  SessionActivityType,
  SessionEstimateItemUnitType,
} from '@talon/web/lib/api';
import { useEstimateStore } from '@talon/web/src/context';
import { shouldClearRolesForCommercialType } from '@talon/web/src/utils/activityRoles';
import { USD } from '@talon/web/src/utils/currency';
import { Box, Button, Select, SelectMultiple, Text } from 'grommet';
import { MapLocation, User, Yoga } from 'grommet-icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useState } from 'react';
import { RoleActivityRow } from '.';
import { Delete, TalonInput, TalonTip } from '../..';
import { ActivityTypeSelect } from './ActivityTypeSelect';

interface ISessionActivityRowProps {
  script?: Script;
  rowIndex: number;
  session: Session;
  activity: SessionActivity;
  roles: Role[];
  locations: Location[];
  deleteSessionActivity: (activityId: string) => Promise<void>;
  updateSessionActivity: (update: Partial<SessionActivity>) => Promise<void>;
}

export const SessionActivityRow = observer(
  ({
    session,
    rowIndex,
    activity,
    roles,
    locations,
    deleteSessionActivity,
    updateSessionActivity,
  }: ISessionActivityRowProps) => {
    /** Context **/
    const estimateStore = useEstimateStore();

    /** State **/
    const [commercialType, setCommercialType] = useState<CommercialType>();
    const [activityTypes, setActivityTypes] = useState<SessionActivityType[]>(Object.values(SessionActivityType));
    const [activityRoles, setActivityRoles] = useState<Role[]>(roles);
    const [, setDisabledTypes] = useState<SessionActivityType[]>([]);
    const [, setDisabledRoles] = useState<Role[]>([]);
    const [showLocation, setShowLocation] = useState(false);
    const [straightHours, setStraightHours] = useState(0);
    const [overtimeHours, setOvertimeHours] = useState(0);
    const [wages, setWages] = useState(0);
    const [total, setTotal] = useState(0);

    /** Computed **/
    const sessionEstimateItems = estimateStore.getTempSessionEstimateItems(session.id, activity.id);

    const activityName = `${activity.type}`;

    /** Effects **/

    useEffect(() => {
      if (activity.roles?.length) {
        const disabled: SessionActivityType[] = [];

        let hasPrincipal = false;
        let hasExtra = false;
        let hasAFM = false;

        for (const role of activity.roles) {
          if (getIsPrincipal(role.performer_type)) hasPrincipal = true;
          if (getIsExtra(role.performer_type)) hasExtra = true;
          if (getIsAFMPerformer(role.performer_type)) hasAFM = true;
        }

        const types = Object.values(SessionActivityType).filter((type) => {
          if (type === SessionActivityType.CreativeAudition || type === SessionActivityType.CreativeSession) {
            if (hasExtra || hasAFM) disabled.push(type);
          } else if (type === SessionActivityType.WardrobeChange) {
            if (!activity.costumes?.length) disabled.push(type);
          } else if (type === SessionActivityType.WorkTimeLateNight) {
            if (hasPrincipal || hasAFM) disabled.push(type);
          } else if (type.toLocaleLowerCase().includes('meal')) {
            if (hasExtra || hasAFM) disabled.push(type);
          } else if (type === SessionActivityType.AFMRecordingSession) {
            if (hasPrincipal || hasExtra) disabled.push(type);
          }

          return disabled[disabled.length - 1] !== type;
        });

        setActivityTypes(types);
        setDisabledTypes(disabled);
      } else {
        setActivityTypes(Object.values(SessionActivityType));
        setDisabledTypes([]);
      }
    }, [activity.costumes, activity.location, activity.roles]);

    useEffect(() => {
      if (activity.type) {
        const disabled: Role[] = [];

        const r = roles
          .filter((role) => {
            const isPrincipal = getIsPrincipal(role.performer_type);
            const isExtra = getIsExtra(role.performer_type);
            const isAFM = getIsAFMPerformer(role.performer_type);

            if (
              activity.type === SessionActivityType.CreativeAudition ||
              activity.type === SessionActivityType.CreativeSession
            ) {
              if (!isPrincipal) disabled.push(role);
            } else if (activity.type === SessionActivityType.WorkTimeLateNight) {
              if (!isExtra) disabled.push(role);
            } else if (activity.type?.toLocaleLowerCase().includes('meal')) {
              if (!isPrincipal) disabled.push(role);
            } else if (activity.type === SessionActivityType.AFMRecordingSession) {
              if (!isAFM) disabled.push(role);
            }

            return disabled[disabled.length - 1] !== role;
          })
          .sort((a, b) => a.name.localeCompare(b.name));

        setActivityRoles(r);
        setDisabledRoles(disabled);
      }
    }, [activity.type, roles]);

    useEffect(() => {
      if (commercialType !== session.commercial_type) {
        // Only an actual change of the session's commercial type can invalidate roles - never the
        // first render, which just records the type (see shouldClearRolesForCommercialType)
        if (shouldClearRolesForCommercialType(commercialType, session.commercial_type, activity.roles)) {
          updateSessionActivity({ roles: [] });
        }

        setCommercialType(session.commercial_type);
      }
    }, [commercialType, session.commercial_type, activity.roles]);

    useEffect(() => {
      let st = 0;
      let ot = 0;
      let wages = 0;
      let total = 0;

      if (sessionEstimateItems) {
        for (const item of sessionEstimateItems) {
          if (item.unit_type === SessionEstimateItemUnitType.Hours) {
            const title = item.title.toLocaleLowerCase();
            if (title.includes(sessionEstimateItemTitles[WorkTimeType.StraightTime].toLocaleLowerCase()))
              st += item.units ?? 0;
            if (
              title.includes(sessionEstimateItemTitles[WorkTimeType.TimeAndOneHalf].toLocaleLowerCase()) ||
              title.includes(sessionEstimateItemTitles[WorkTimeType.DoubleTime].toLocaleLowerCase())
            )
              ot += item.units ?? 0;
          }
          wages += item.subtotal;
          total += item.subtotal_plus_fringe;
        }
      }

      setStraightHours(st);
      setOvertimeHours(ot);
      setWages(wages);
      setTotal(total);
    }, [sessionEstimateItems]);

    /** Render **/

    const renderHoursItem = (hours: number) => (
      <TalonTip
        content={
          <Box direction="row" gap="xxsmall">
            <Text size="small">
              {activity.hours ?? 0} hr{(activity.hours ?? 0) > 1 && 's'} x {activity.roles?.length ?? 0} role
              {(activity.roles?.length ?? 0) > 1 && 's'}
            </Text>
            <Text size="small">=</Text>
            <Text size="small" weight={600}>
              {hours} hr{(hours ?? 0) > 1 && 's'}
            </Text>
          </Box>
        }
      >
        <Text size="xsmall" weight={600}>
          {hours} hr{(hours ?? 0) > 1 && 's'}
        </Text>
      </TalonTip>
    );

    return activity ? (
      <Box
        direction="row"
        gap="small"
        align="center"
        pad="small"
        border={rowIndex > 0 && { side: 'top', color: 'light-3' }}
      >
        <Box width="6%">
          <TalonInput
            val={activity.hours ?? 0}
            inputType="number"
            placeholder="Session length..."
            onChange={(hours) => {
              const h = parseFloat(hours);
              updateSessionActivity({
                hours: !Number.isNaN(h) ? h : 0,
              });
            }}
          />
        </Box>
        <Box width="16%">
          <SelectMultiple
            options={activityRoles}
            value={activity.roles?.reduce((acc, curr) => (curr.role_group ? acc : acc.concat(curr)), [] as Role[])}
            valueKey="id"
            labelKey="name"
            placeholder="Choose roles..."
            size="medium"
            onChange={({ value }) => updateSessionActivity({ roles: value })}
            icon={
              <TalonTip content={<Text>Choose Roles</Text>}>
                <User />
              </TalonTip>
            }
            valueLabel={(values) => (
              <Box justify="center" fill="horizontal">
                {values && values.length ? (
                  <Box pad={{ vertical: 'xsmall' }}>
                    {values.map((role: Role) => {
                      return (
                        <RoleActivityRow
                          roleId={role.id}
                          activity={activity}
                          key={role.id}
                          updateSessionActivity={updateSessionActivity}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Box pad={{ horizontal: 'small', vertical: 'small' }}>
                    <Text size="small">Select Roles</Text>
                  </Box>
                )}
              </Box>
            )}
          />
        </Box>
        <Box width="20%">
          <ActivityTypeSelect
            options={activityTypes}
            val={activity.type}
            placeholder="Choose activity type..."
            size="xsmall"
            onChange={(type) => updateSessionActivity({ type })}
            icon={
              <TalonTip content={<Text>Choose Activity Type</Text>}>
                <Yoga />
              </TalonTip>
            }
          />
        </Box>
        <Box direction="row" justify="start" gap="small" align="center" pad={{ vertical: 'xsmall' }} width="16%">
          {activity.type && !TravelActivityTypes.has(activity.type) && (
            <Box gap="small">
              {!showLocation && !activity.location && !MealActivityTypes.has(activity.type) && (
                <Button plain onClick={() => setShowLocation(true)}>
                  <Text size="small">Add Location</Text>
                </Button>
              )}
              {(showLocation || activity.location || MealActivityTypes.has(activity.type)) && (
                <Select
                  options={locations}
                  value={activity.location}
                  valueKey="id"
                  labelKey="name"
                  placeholder="Choose location..."
                  size="xsmall"
                  icon={
                    <TalonTip content={<Text>Choose Location</Text>}>
                      <MapLocation />
                    </TalonTip>
                  }
                  onChange={({ value }) => updateSessionActivity({ location: value })}
                />
              )}
            </Box>
          )}
          {activity.type && TravelActivityTypes.has(activity.type) && (
            <Box gap="small">
              <Box>
                <Select
                  options={locations.filter((l) => l.id !== activity.to_location?.id)}
                  value={activity.from_location}
                  valueKey="id"
                  labelKey="name"
                  placeholder="Choose start location..."
                  size="xsmall"
                  icon={
                    <TalonTip content={<Text>Choose From Location</Text>}>
                      <MapLocation />
                    </TalonTip>
                  }
                  onChange={({ value }) => updateSessionActivity({ from_location: value })}
                />
              </Box>
              <Box>
                <Select
                  options={locations.filter((l) => l.id !== activity.from_location?.id)}
                  value={activity.to_location}
                  valueKey="id"
                  labelKey="name"
                  placeholder="Choose destination location..."
                  size="xsmall"
                  icon={
                    <TalonTip content={<Text>Choose To Location</Text>}>
                      <MapLocation />
                    </TalonTip>
                  }
                  onChange={({ value }) => updateSessionActivity({ to_location: value })}
                />
              </Box>
            </Box>
          )}
        </Box>
        {/* <Box flex /> */}
        <Box align="start" flex="grow">
          {WorkActivityTypes.has(activity.type) && (
            <Box>
              {!!straightHours && (
                <Box direction="row" justify="between" fill="horizontal" gap="xsmall">
                  <Text size="xsmall">STRAIGHT TIME:</Text>
                  {renderHoursItem(straightHours)}
                </Box>
              )}
              {!!overtimeHours && (
                <Box direction="row" justify="between" fill="horizontal" gap="xsmall">
                  <Text size="xsmall">OVERTIME:</Text>
                  {renderHoursItem(overtimeHours)}
                </Box>
              )}
            </Box>
          )}
        </Box>
        <Box align="end" width="10%">
          <Text weight={500}>{USD(wages)}</Text>
        </Box>
        <Box align="end" width="10%">
          <Text weight={500}>{USD(total)}</Text>
        </Box>

        <Box width="48px" height="48px" align="center" justify="center">
          <Delete onClick={() => deleteSessionActivity(activity.id)} name={activityName} />
        </Box>
      </Box>
    ) : (
      <></>
    );
  }
);
